import * as React from 'react';
import bindAllOfThis from "src/utils/BindThisHelper";
import { withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';
import DistrictApiController from 'src/api/DistrictApiController';
import DistrictSyncStatusDTO from 'src/models/district/DistrictSyncStatusDTO';
import BulkUpdateDistrictSyncDTO from 'src/models/district/BulkUpdateDistrictSyncDTO';
import { Table, Button, Checkbox, Input, Tabs, message, Modal, Row, Col, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import "./SyncManagement.less";

const { TabPane } = Tabs;
const { Search } = Input;
const { confirm } = Modal;

interface SyncManagementProps extends PageProps<{}>, LocalizeContextProps {
}

interface SyncManagementState {
    districts: DistrictSyncStatusDTO[];
    filteredDistricts: DistrictSyncStatusDTO[];
    selectedDistrictIds: number[];
    activeTab: 'clever' | 'classlink';
    loading: boolean;
    searchText: string;
    filterStatus: 'all' | 'syncing' | 'enabled' | 'disabled';
    updating: boolean;
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc' | null;
}

class SyncManagement extends React.Component<SyncManagementProps, SyncManagementState> {
    constructor(props: SyncManagementProps) {
        super(props);
        bindAllOfThis(this, SyncManagement.prototype);

        this.state = {
            districts: [],
            filteredDistricts: [],
            selectedDistrictIds: [],
            activeTab: 'clever',
            loading: true,
            searchText: '',
            filterStatus: 'all',
            updating: false
        };
    }

    componentDidMount() {
        this.loadDistricts();
    }

    private renderSortableTitle(displayName: string, columnKey: string) {
        const { sortColumn, sortDirection } = this.state as SyncManagementState;
        const isActive = sortColumn === columnKey;
        const arrow = isActive ? (sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '⇅') : '⇅';

        return (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ marginRight: 8 }}>{displayName}</span>
                <a style={{ cursor: 'pointer', color: isActive ? '#1890ff' : undefined }} onClick={() => this.toggleSort(columnKey)}>{arrow}</a>
            </span>
        );
    }

    private toggleSort(columnKey: string) {
        const { sortColumn, sortDirection } = this.state as SyncManagementState;
        let newDirection: 'asc' | 'desc' | null = 'asc';
        if (sortColumn === columnKey) {
            if (sortDirection === 'asc') {
                newDirection = 'desc';
            } else if (sortDirection === 'desc') {
                newDirection = null;
            } else {
                newDirection = 'asc';
            }
        } else {
            newDirection = 'asc';
        }

        this.setState({ sortColumn: newDirection ? columnKey : null, sortDirection: newDirection }, () => {
            this.sortFilteredDistricts();
        });
    }

    private sortFilteredDistricts() {
        const { sortColumn, sortDirection, filteredDistricts } = this.state as SyncManagementState & { filteredDistricts: DistrictSyncStatusDTO[] };
        if (!sortColumn || !sortDirection) {
            // If no sort, reload original filtered list from districts and current filters
            this.filterDistricts();
            return;
        }

        const sorted = [...filteredDistricts].sort((a: any, b: any) => {
            const av = this.getFieldValue(a, sortColumn);
            const bv = this.getFieldValue(b, sortColumn);

            const cmp = this.compareValues(av, bv);
            return sortDirection === 'asc' ? cmp : -cmp;
        });

        this.setState({ filteredDistricts: sorted });
    }

    private getFieldValue(obj: any, field: string) {
        if (!obj) {
            return null;
        }
        return obj[field];
    }

    private compareValues(a: any, b: any) {
        if (a == null && b == null) {
            return 0;
        }
        if (a == null) {
            return -1;
        }
        if (b == null) {
            return 1;
        }

        // Moment objects (from moment.js)
        if (a && typeof a === 'object' && 'isValid' in a && 'valueOf' in a) {
            if (b && typeof b === 'object' && 'isValid' in b && 'valueOf' in b) {
                if (a.isValid() && b.isValid()) {
                    return a.valueOf() - b.valueOf();
                }
                if (!a.isValid()) {
                    return -1;
                }
                if (!b.isValid()) {
                    return 1;
                }
            }
        }

        // Dates
        const aDate = Date.parse(a);
        const bDate = Date.parse(b);
        if (!isNaN(aDate) && !isNaN(bDate)) {
            return aDate - bDate;
        }

        // Booleans
        if (typeof a === 'boolean' || typeof b === 'boolean') {
            const av = a === true ? 1 : 0;
            const bv = b === true ? 1 : 0;
            return av - bv;
        }

        // Numbers
        if (!isNaN(Number(a)) && !isNaN(Number(b))) {
            return Number(a) - Number(b);
        }

        // Fallback to string comparison
        try {
            return String(a).localeCompare(String(b));
        } catch (e) {
            return 0;
        }
    }

    private loadDistricts() {
        this.setState({ loading: true });
        DistrictApiController.getAllDistrictsWithSyncStatus()
            .then(results => {
                if (results.ok && results.data) {
                    this.setState({
                        districts: results.data,
                        filteredDistricts: results.data,
                        loading: false
                    });
                    this.filterDistricts();
                } else {
                    message.error(this.props.translate("admin.syncManagement.loadError") as string || "Failed to load districts");
                    this.setState({ loading: false });
                }
            })
            .catch(_error => {
                message.error(this.props.translate("admin.syncManagement.loadError") as string || "Failed to load districts");
                this.setState({ loading: false });
            });
    }

    private filterDistricts() {
        const { districts, searchText, filterStatus, activeTab } = this.state;
        let filtered = [...districts];

        // Filter by active tab (Clever or ClassLink)
        if (activeTab === 'clever') {
            filtered = filtered.filter(d => d.cleverDistrictId && d.cleverDistrictId.trim() !== '');
        } else {
            filtered = filtered.filter(d => d.hasClassLinkConfiguration);
        }

        // Filter by search text
        if (searchText) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(d =>
                d.districtName.toLowerCase().includes(searchLower) ||
                (d.cleverDistrictId && d.cleverDistrictId.toLowerCase().includes(searchLower)) ||
                (d.classLinkTenantId && d.classLinkTenantId.toString().includes(searchLower))
            );
        }

        // Filter by status
        if (filterStatus !== 'all') {
            if (activeTab === 'clever') {
                if (filterStatus === 'syncing') {
                    filtered = filtered.filter(d => d.isSyncRunning);
                } else if (filterStatus === 'enabled') {
                    filtered = filtered.filter(d => d.fullSync === true);
                } else if (filterStatus === 'disabled') {
                    filtered = filtered.filter(d => d.fullSync === false || d.fullSync == null);
                }
            } else {
                if (filterStatus === 'syncing') {
                    filtered = filtered.filter(d => d.isSyncRunning);
                } else if (filterStatus === 'enabled') {
                    filtered = filtered.filter(d => d.syncClassLinkStudentsAndTeachers === true);
                } else if (filterStatus === 'disabled') {
                    filtered = filtered.filter(d => d.syncClassLinkStudentsAndTeachers === false || d.syncClassLinkStudentsAndTeachers == null);
                }
            }
        }

        this.setState({ filteredDistricts: filtered }, () => {
            const { sortColumn, sortDirection } = this.state as SyncManagementState;
            if (sortColumn && sortDirection) {
                this.sortFilteredDistricts();
            }
        });
    }

    private handleSearch = (value: string) => {
        this.setState({ searchText: value }, () => {
            this.filterDistricts();
        });
    }

    private handleFilterStatusChange = (value: 'all' | 'syncing' | 'enabled' | 'disabled') => {
        this.setState({ filterStatus: value }, () => {
            this.filterDistricts();
        });
    }

    private handleTabChange = (activeKey: string) => {
        const newTab = activeKey as 'clever' | 'classlink';
        this.setState({ activeTab: newTab, selectedDistrictIds: [] }, () => {
            this.filterDistricts();
        });
    }

    private handleSelectDistrict = (districtId: number, checked: boolean) => {
        const { selectedDistrictIds } = this.state;
        if (checked) {
            this.setState({ selectedDistrictIds: [...selectedDistrictIds, districtId] });
        } else {
            this.setState({ selectedDistrictIds: selectedDistrictIds.filter(id => id !== districtId) });
        }
    }

    private handleSelectAll = () => {
        const { filteredDistricts } = this.state;
        const allIds = filteredDistricts.map(d => d.districtId);
        this.setState({ selectedDistrictIds: allIds });
    }

    private handleDeselectAll = () => {
        this.setState({ selectedDistrictIds: [] });
    }

    private getSelectedDistricts(): DistrictSyncStatusDTO[] {
        const { districts, selectedDistrictIds } = this.state;
        return districts.filter(d => selectedDistrictIds.includes(d.districtId));
    }

    private handleBulkUpdate = (action: 'enableFullSync' | 'disableFullSync' | 'enableClassLinkSync' | 'disableClassLinkSync') => {
        const selected = this.getSelectedDistricts();
        if (selected.length === 0) {
            message.warning(this.props.translate("admin.syncManagement.noDistrictsSelected") as string || "Please select at least one district");
            return;
        }

        const districtIds = selected.map(d => d.districtId);
        const count = districtIds.length;
        const confirmText = (this.props.translate("admin.syncManagement.confirmBulkUpdate") as string || `Are you sure you want to update {count} district(s)?`).replace("{count}", count.toString());

        confirm({
            title: confirmText,
            onOk: () => {
                this.performBulkUpdate(action, districtIds);
            }
        });
    }

    private toggleSyncSetting(districtId: number, setting: 'syncStudents' | 'syncTeachers', currentValue: boolean) {
        this.setState({ updating: true });

        const dto: BulkUpdateDistrictSyncDTO = {
            districtIds: [districtId]
        };

        if (setting === 'syncStudents') {
            dto.syncStudents = !currentValue;
        } else {
            dto.syncTeachers = !currentValue;
        }

        DistrictApiController.bulkUpdateCleverSync(dto)
            .then(results => {
                if (results.ok) {
                    message.success("Successfully updated district");
                    this.setState({ updating: false });
                    this.loadDistricts();
                } else {
                    message.error(this.props.translate("admin.syncManagement.updateError") as string || "Failed to update district");
                    this.setState({ updating: false });
                }
            })
            .catch(_error => {
                message.error(this.props.translate("admin.syncManagement.updateError") as string || "Failed to update district");
                this.setState({ updating: false });
            });
    }

    private performBulkUpdate(action: 'enableFullSync' | 'disableFullSync' | 'enableClassLinkSync' | 'disableClassLinkSync', districtIds: number[]) {
        this.setState({ updating: true });

        const dto: BulkUpdateDistrictSyncDTO = {
            districtIds: districtIds
        };

        let apiCall: Promise<any>;

        if (action === 'enableFullSync') {
            dto.fullSync = true;
            dto.syncStudents = true;
            dto.syncTeachers = true;
            apiCall = DistrictApiController.bulkUpdateCleverSync(dto);
        } else if (action === 'disableFullSync') {
            dto.fullSync = false;
            apiCall = DistrictApiController.bulkUpdateCleverSync(dto);
        } else if (action === 'enableClassLinkSync') {
            dto.syncClassLinkStudentsAndTeachers = true;
            apiCall = DistrictApiController.bulkUpdateClassLinkSync(dto);
        } else {
            dto.syncClassLinkStudentsAndTeachers = false;
            apiCall = DistrictApiController.bulkUpdateClassLinkSync(dto);
        }

        apiCall
            .then(results => {
                if (results.ok) {
                    const successText = (this.props.translate("admin.syncManagement.updateSuccess") as string || `Successfully updated {count} district(s)`).replace("{count}", districtIds.length.toString());
                    message.success(successText);
                    this.setState({ selectedDistrictIds: [], updating: false });
                    this.loadDistricts();
                } else {
                    message.error(this.props.translate("admin.syncManagement.updateError") as string || "Failed to update districts");
                    this.setState({ updating: false });
                }
            })
            .catch(_error => {
                message.error(this.props.translate("admin.syncManagement.updateError") as string || "Failed to update districts");
                this.setState({ updating: false });
            });
    }

    private getCleverTableColumns(): ColumnProps<DistrictSyncStatusDTO>[] {
        const { selectedDistrictIds } = this.state;

        return [
            {
                title: '',
                key: 'checkbox',
                width: 50,
                render: (text, record) => (
                    <Checkbox
                        checked={selectedDistrictIds.includes(record.districtId)}
                        onChange={(e) => this.handleSelectDistrict(record.districtId, e.target.checked)}
                    />
                )
            },
            {
                title: this.renderSortableTitle((this.props.translate("admin.syncManagement.districtName") as string) || "District Name", 'districtName'),
                dataIndex: 'districtName',
                key: 'districtName'
            },
            {
                title: this.renderSortableTitle((this.props.translate("admin.syncManagement.cleverDistrictId") as string) || "Clever District ID", 'cleverDistrictId'),
                dataIndex: 'cleverDistrictId',
                key: 'cleverDistrictId'
            },
            {
                title: this.renderSortableTitle((this.props.translate("admin.syncManagement.syncStudents") as string) || "Sync Students", 'syncStudents'),
                dataIndex: 'syncStudents',
                key: 'syncStudents',
                render: (value: boolean, record: DistrictSyncStatusDTO) => {
                    const isEnabled = value === true;
                    return (
                        <Tag 
                            color={isEnabled ? "green" : "default"}
                            style={{ cursor: 'pointer' }}
                            onClick={() => this.toggleSyncSetting(record.districtId, 'syncStudents', isEnabled)}
                        >
                            {isEnabled ? "Yes" : "No"}
                        </Tag>
                    );
                }
            },
            {
                title: this.renderSortableTitle((this.props.translate("admin.syncManagement.syncTeachers") as string) || "Sync Teachers", 'syncTeachers'),
                dataIndex: 'syncTeachers',
                key: 'syncTeachers',
                render: (value: boolean, record: DistrictSyncStatusDTO) => {
                    const isEnabled = value === true;
                    return (
                        <Tag 
                            color={isEnabled ? "green" : "default"}
                            style={{ cursor: 'pointer' }}
                            onClick={() => this.toggleSyncSetting(record.districtId, 'syncTeachers', isEnabled)}
                        >
                            {isEnabled ? "Yes" : "No"}
                        </Tag>
                    );
                }
            },
            {
                title: this.renderSortableTitle((this.props.translate("admin.syncManagement.fullSync") as string) || "Full Sync", 'fullSync'),
                dataIndex: 'fullSync',
                key: 'fullSync',
                render: (value: boolean) => {
                    if (value === true) {
                        return <Tag color="green">Enabled</Tag>;
                    }
                    if (value === false) {
                        return <Tag color="red">Disabled</Tag>;
                    }
                    return <Tag color="default">Not Set</Tag>;
                }
            },
            {
                title: this.renderSortableTitle((this.props.translate("admin.syncManagement.lastSyncStudents") as string) || "Last Student Sync", 'fullSyncEndTimeStudents'),
                dataIndex: 'fullSyncEndTimeStudents',
                key: 'fullSyncEndTimeStudents',
                render: (value: any) => value ? new Date(value).toLocaleString() : '-'
            },
            {
                title: this.renderSortableTitle((this.props.translate("admin.syncManagement.lastSyncTeachers") as string) || "Last Teacher Sync", 'fullSyncEndTimeTeachers'),
                dataIndex: 'fullSyncEndTimeTeachers',
                key: 'fullSyncEndTimeTeachers',
                render: (value: any) => value ? new Date(value).toLocaleString() : '-'
            }
        ];
    }

    private getClassLinkTableColumns(): ColumnProps<DistrictSyncStatusDTO>[] {
        const { selectedDistrictIds } = this.state;

        return [
            {
                title: '',
                key: 'checkbox',
                width: 50,
                render: (text, record) => (
                    <Checkbox
                        checked={selectedDistrictIds.includes(record.districtId)}
                        onChange={(e) => this.handleSelectDistrict(record.districtId, e.target.checked)}
                    />
                )
            },
            {
                title: this.renderSortableTitle((this.props.translate("admin.syncManagement.districtName") as string) || "District Name", 'districtName'),
                dataIndex: 'districtName',
                key: 'districtName'
            },
            {
                title: this.renderSortableTitle((this.props.translate("admin.syncManagement.classLinkTenantId") as string) || "ClassLink Tenant ID", 'classLinkTenantId'),
                dataIndex: 'classLinkTenantId',
                key: 'classLinkTenantId',
                render: (value: number) => value || '-'
            },
            {
                title: this.renderSortableTitle((this.props.translate("admin.syncManagement.syncClassLinkStudentsAndTeachers") as string) || "Sync ClassLink", 'syncClassLinkStudentsAndTeachers'),
                dataIndex: 'syncClassLinkStudentsAndTeachers',
                key: 'syncClassLinkStudentsAndTeachers',
                render: (value: boolean) => {
                    if (value === true) {
                        return <Tag color="green">Enabled</Tag>;
                    }
                    if (value === false) {
                        return <Tag color="red">Disabled</Tag>;
                    }
                    return <Tag color="default">Not Set</Tag>;
                }
            }
        ];
    }

    render() {
        const { filteredDistricts, selectedDistrictIds, activeTab, loading, updating } = this.state;

        return (
            <div className="sync-management">
                <h2>{this.props.translate("admin.syncManagement.pageTitle") || "District Sync Management"}</h2>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Search
                            placeholder={(this.props.translate("admin.syncManagement.filterByDistrict") as string) || "Filter by district name"}
                            onSearch={this.handleSearch}
                            onChange={(e) => this.handleSearch(e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>

                <Tabs activeKey={activeTab} onChange={this.handleTabChange}>
                    <TabPane tab={this.props.translate("admin.syncManagement.cleverTab") || "Clever Sync"} key="clever">
                        <div style={{ marginBottom: 16 }}>
                            <Button
                                type="primary"
                                onClick={() => this.handleBulkUpdate('enableFullSync')}
                                disabled={selectedDistrictIds.length === 0 || updating}
                                style={{ marginRight: 8 }}
                            >
                                {this.props.translate("admin.syncManagement.enableFullSync") || "Enable Full Sync"}
                            </Button>
                            <Button
                                onClick={() => this.handleBulkUpdate('disableFullSync')}
                                disabled={selectedDistrictIds.length === 0 || updating}
                            >
                                {this.props.translate("admin.syncManagement.disableFullSync") || "Disable Full Sync"}
                            </Button>
                        </div>
                        <Table
                            columns={this.getCleverTableColumns()}
                            dataSource={filteredDistricts}
                            rowKey="districtId"
                            loading={loading || updating}
                            pagination={{ pageSize: 50 }}
                        />
                    </TabPane>
                    <TabPane tab={this.props.translate("admin.syncManagement.classLinkTab") || "ClassLink Sync"} key="classlink">
                        <div style={{ marginBottom: 16 }}>
                            <Button
                                onClick={this.handleSelectAll}
                                style={{ marginRight: 8 }}
                            >
                                {this.props.translate("admin.syncManagement.selectAll") || "Select All"}
                            </Button>
                            <Button
                                onClick={this.handleDeselectAll}
                                style={{ marginRight: 16 }}
                            >
                                {this.props.translate("admin.syncManagement.deselectAll") || "Deselect All"}
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => this.handleBulkUpdate('enableClassLinkSync')}
                                disabled={selectedDistrictIds.length === 0 || updating}
                                style={{ marginRight: 8 }}
                            >
                                {this.props.translate("admin.syncManagement.enableClassLinkSync") || "Enable ClassLink Sync"}
                            </Button>
                            <Button
                                onClick={() => this.handleBulkUpdate('disableClassLinkSync')}
                                disabled={selectedDistrictIds.length === 0 || updating}
                            >
                                {this.props.translate("admin.syncManagement.disableClassLinkSync") || "Disable ClassLink Sync"}
                            </Button>
                        </div>
                        <Table
                            columns={this.getClassLinkTableColumns()}
                            dataSource={filteredDistricts}
                            rowKey="districtId"
                            loading={loading || updating}
                            pagination={{ pageSize: 50 }}
                        />
                    </TabPane>
                </Tabs>
            </div>
        );
    }
}

export default withLocalize(SyncManagement);




// WEBPACK FOOTER //
// ./src/pages/admin/SyncManagement.tsx